import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "convex/react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

import logo from "@/assets/logo.webp";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CreditKeyOrderPanel } from "@/components/internal/credit-key/CreditKeyOrderPanel";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/lib/auth-client";
import { formatStatusLabel, isPayWhenReadySentinelDate } from "@/lib/format";
import { getOrderStatusBadgeClass } from "@/lib/status-tag-colors";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { api } from "$convex/convexApi";
import {
  getShippingQuoteBreakdown,
  SHIPPING_MARKUP_POLICY_PCT,
} from "$convex/helpers/shippingQuote";
import "./page.css";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt$(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "$0.00";
  const prefix = n < 0 ? "-$" : "$";
  return `${prefix}${Math.abs(n).toFixed(2)}`;
}

function fmtDate(ts: number | null | undefined): string {
  if (!ts) return "";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(ts));
}

function fmtShortDate(ts: number | null | undefined): string {
  if (!ts) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  }).format(new Date(ts));
}

function fmtDateTime(ts: number | null | undefined): string {
  if (!ts) return "";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(ts));
}

function formatResolutionMethod(method: string | null | undefined): string {
  switch (method) {
    case "points_credit":
      return "Points credit";
    case "scheduled_payment_updated":
      return "Scheduled payment updated";
    case "points_deducted":
      return "Points applied";
    case "points_partial_link":
      return "Points and payment link";
    case "payment_link":
      return "Payment link";
    case "absorbed_under_5":
      return "Absorbed by Anthem";
    default:
      return method
        ? method
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        : "Not recorded";
  }
}

function formatPaymentMethod(method: string | null | undefined): string {
  if (!method) return "Not specified";
  const lm = method.toLowerCase();
  if (lm.includes("ach") || lm.includes("bank") || lm === "us_bank_account") return "ACH Bank Transfer";
  if (lm.includes("card")) return "Credit/Debit Card";
  if (lm === "credit") return "Credit Terms";
  if (lm.includes("wire")) return "Wire Transfer";
  if (lm.includes("check") || lm.includes("cheque")) return "Check";
  if (lm.includes("cash")) return "Cash";
  return method.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/** Credit-terms orders: show linked rail (ACH vs card) from staff invoice payload. */
function formatInternalInvoicePaymentMethod(
  payment: { method?: string; instrumentType?: string; instrumentTypeAssumed?: boolean } | null | undefined,
): string {
  if (payment?.instrumentTypeAssumed) return "Pay later — payment method not selected";
  const m = payment?.method;
  if ((m === "cheque" || m === "check") && payment?.instrumentType && payment.instrumentType !== "offline") {
    return formatPaymentMethod(payment.instrumentType);
  }
  if (m === "credit" && payment?.instrumentType) {
    const inst = payment.instrumentType;
    if (inst === "us_bank_account") return "Credit terms — ACH Bank Transfer";
    if (inst === "card") return "Credit terms — Credit/Debit Card";
    if (inst === "paypal") return "Credit terms — PayPal";
    if (inst === "offline") return "Credit terms — Offline payment";
  }
  return formatPaymentMethod(m);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function InvoiceDetailPage() {
  const { id: orderId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const invoiceContentRef = React.useRef<HTMLDivElement | null>(null);

  const { data: session, isPending: isAuthLoading } = useSession();
  const authUserId = (session?.user as { id?: string } | undefined)?.id;

  // ── STAFF-ONLY query — requires accounting:view ──
  const data = useQuery(
    api.admin.getInternalInvoiceData,
    !isAuthLoading && authUserId && orderId
      ? { authUserId, orderId: orderId as any }
      : "skip",
  ) as any;

  const isLoading = Boolean(isAuthLoading || (orderId && authUserId && data === undefined));

  // ── Derived values ──
  const items = data?.items ?? [];
  const user = data?.user;
  const payment = data?.payment;
  const paymentReconciliation = data?.paymentReconciliation;
  const auditEntries = data?.auditEntries ?? [];
  const shipping = data?.shipping;
  const totals = data?.totals;

  const isPaid = payment?.status === "paid" || payment?.status === "completed" || payment?.status === "succeeded";
  const hasReconciliationException = Boolean(
    paymentReconciliation &&
      !["matched", "not_collected"].includes(paymentReconciliation.status),
  );

  const formattedDate = React.useMemo(() => fmtDate(data?.createdAt), [data?.createdAt]);

  // Scheduled payment info. Pay-when-ready obligations carry a sentinel
  // scheduledDate and are not auto-charged, so they don't count here.
  const nextScheduledDate =
    payment?.nextScheduledDate && !isPayWhenReadySentinelDate(payment.nextScheduledDate)
      ? payment.nextScheduledDate
      : null;
  const isScheduledPayment = Boolean(nextScheduledDate);

  const shippingQuote = React.useMemo(() => getShippingQuoteBreakdown({
    actualBaseCost: shipping?.actualBaseCost,
    chargedToCustomer: shipping?.quotedToCustomer,
  }), [shipping]);

  const hasShippingDetails = Boolean(
    shipping && (
      shipping.quoteId ||
      shipping.accessorialServices?.length > 0 ||
      shipping.quotedToCustomer > 0 ||
      shipping.actualBaseCost != null ||
      shipping.pickupDate
    ),
  );

  const showShippingCharged = Boolean(shipping && shipping.quotedToCustomer > 0);

  // ── Print / PDF ──
  const printInvoice = React.useCallback(() => { window.print(); }, []);

  const generatePDF = React.useCallback(async () => {
    try {
      const hiddenElements = Array.from(document.querySelectorAll<HTMLElement>("[data-print-hide='true']"));
      const previousDisplay = hiddenElements.map((el) => el.style.display);
      hiddenElements.forEach((el) => { el.style.display = "none"; });

      if (!invoiceContentRef.current) throw new Error("Invoice content not found");

      const canvas = await html2canvas(invoiceContentRef.current, {
        scale: 2, useCORS: true, logging: false, windowWidth: 1200, backgroundColor: "#ffffff",
      });

      hiddenElements.forEach((el, i) => { el.style.display = previousDisplay[i] || ""; });

      const imageData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const imageWidth = 210;
      const pageHeight = 297;
      const imageHeight = (canvas.height * imageWidth) / canvas.width;
      let heightLeft = imageHeight;
      let position = 0;

      pdf.addImage(imageData, "PNG", 0, position, imageWidth, imageHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imageHeight;
        pdf.addPage();
        pdf.addImage(imageData, "PNG", 0, position, imageWidth, imageHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`invoice-${data?.orderNumber || orderId}.pdf`);
    } catch {
      window.alert("Failed to generate PDF. Please try again.");
    }
  }, [data?.orderNumber, orderId]);

  // Auto-print / auto-download via query params
  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = `Invoice ${data?.orderNumber || ""}`;
    return () => { document.title = previousTitle; };
  }, [data?.orderNumber]);

  React.useEffect(() => {
    if (searchParams.get("print") === "true") {
      const t = window.setTimeout(() => window.print(), 1000);
      return () => window.clearTimeout(t);
    }
    if (searchParams.get("download") === "true") {
      const t = window.setTimeout(() => void generatePDF(), 1000);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [generatePDF, searchParams]);

  // ── Guards ──
  if (!orderId) {
    return (
      <main className="invoice-page container mx-auto flex min-h-screen max-w-4xl flex-col justify-center p-4 py-20">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-red-800">Missing order ID</p>
        </div>
      </main>
    );
  }

  if (!authUserId && !isAuthLoading) {
    return (
      <main className="invoice-page container mx-auto flex min-h-screen max-w-4xl flex-col justify-center p-4 py-20">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-red-800">Authentication required to view this invoice.</p>
        </div>
      </main>
    );
  }

  if (isLoading) {
    return (
      <main className="invoice-page container mx-auto flex min-h-screen max-w-4xl flex-col justify-center p-4 py-20 print:p-0 print:py-0">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="invoice-page container mx-auto flex min-h-screen max-w-4xl flex-col justify-center p-4 py-20">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
          <p className="text-gray-600">Invoice not found or access denied.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="invoice-page container mx-auto flex min-h-screen max-w-4xl flex-col justify-center p-4 py-20 print:p-0 print:py-0">
      {/* ── Action bar (hidden in print) ── */}
      <div className="mb-4 flex items-center gap-2" data-print-hide="true">
        <Button variant="outline" onClick={() => window.history.back()}>
          &larr; Back
        </Button>
        <Button className="bg-black text-white hover:bg-gray-800" onClick={printInvoice}>
          Print Invoice
        </Button>
        <Button className="bg-black text-white hover:bg-gray-800" onClick={() => void generatePDF()}>
          Download PDF
        </Button>
      </div>

      {orderId ? <CreditKeyOrderPanel orderId={orderId} /> : null}

      <div
        ref={invoiceContentRef}
        className="invoice-content relative rounded-sm border border-gray-200 bg-white p-8 shadow-sm print:border-0 print:p-6 print:shadow-none"
      >
        {/* Cancelled watermark */}
        {data.status?.toLowerCase() === "cancelled" && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="-rotate-[30deg] border-8 border-red-500 px-8 py-4 text-7xl font-bold uppercase text-red-500 opacity-20">
              Cancelled
            </div>
          </div>
        )}

        {/* ────────────────────────────── HEADER ────────────────────────────── */}
        <div className="mb-8 flex flex-col justify-between gap-6 md:flex-row print:flex-row">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <img src={logo} alt="Anthem" className="h-10 w-auto" />
              <h1 className="text-3xl font-bold text-gray-900">Invoice</h1>
              <Badge variant="secondary" className="bg-slate-900 text-white">
                INTERNAL
              </Badge>
              {data.isEnterprise && (
                <Badge variant="outline" className={getEnterpriseBadgeClass(data.isEnterprise)}>
                  {getEnterpriseBadgeLabel(data.isEnterprise)}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-600">Anthem Coffee Imports</p>
            <p className="text-sm text-gray-500">13615 Wyandotte St., Kansas City, MO 64145</p>
          </div>
          <div className="text-left md:text-right print:text-right">
            <Badge variant="outline" className={getOrderStatusBadgeClass(data.status)}>
              {formatStatusLabel(data.status)}
            </Badge>
            <p className="mt-2 text-xs uppercase tracking-wide text-gray-500">Invoice Number</p>
            <p className="text-2xl font-bold text-gray-900">#{data.orderNumber}</p>
            <p className="mt-1 text-sm text-gray-600">Date: {formattedDate}</p>
          </div>
        </div>

        {/* ──────────────── CUSTOMER & PAYMENT DETAILS ──────────────── */}
        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 print:grid-cols-2">
          {/* Customer */}
          <div>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Customer</h2>
            <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
              {user?.company && <p className="font-semibold text-gray-900">{user.company}</p>}
              {user?.name && <p className="text-gray-700">{user.name}</p>}
              {(user?.shippingAddress || user?.billingAddress) && (
                <p className="mt-2 text-sm text-gray-600">
                  {user.shippingAddress || user.billingAddress}
                  {(user.shippingCity || user.city) && (
                    <>
                      <br />
                      {user.shippingCity || user.city}
                      {(user.shippingState || user.state) ? `, ${user.shippingState || user.state}` : ""}
                      {" "}
                      {user.shippingPostalCode || user.postalCode || ""}
                    </>
                  )}
                </p>
              )}
              {user?.email && <p className="mt-1 text-sm text-gray-600">{user.email}</p>}
            </div>
          </div>

          {/* Payment details */}
          <div>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Payment Details</h2>
            <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm">
                <span className="font-medium">Method:</span>{" "}
                {formatInternalInvoicePaymentMethod(payment)}
                {payment?.methodId && (
                  <span className="ml-1 text-xs text-gray-400">(ID: {payment.methodId})</span>
                )}
                {nextScheduledDate && (
                  <span className="ml-1 text-sm text-gray-600">
                    (Scheduled for {fmtShortDate(nextScheduledDate)})
                  </span>
                )}
              </p>
              <p className="mt-1 text-sm">
                <span className="font-medium">Status:</span>{" "}
                {isPaid ? (
                  <span className="font-medium text-green-600">Paid</span>
                ) : (
                  <span className="font-medium text-gray-900">Unpaid</span>
                )}
              </p>

              {isScheduledPayment && !isPaid && (
                <div className="mt-3 rounded border border-blue-100 bg-blue-50 p-3">
                  <p className="text-sm font-semibold text-blue-800">Scheduled Payment</p>
                  <p className="text-xs text-blue-700">
                    This payment will be automatically processed on the scheduled date using your saved payment method.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ──────────────────────── LINE ITEMS ──────────────────────── */}
        <div className="mb-8 overflow-hidden rounded-md border border-gray-200">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 font-semibold text-gray-700">Product</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Qty</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Price/lb</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length > 0 ? items.map((item: any, idx: number) => (
                <tr key={item._id || idx}>
                  <td className="px-4 py-4">
                    <p className="font-medium text-gray-900">
                      {item.productName}
                      {item.ociNumber && (
                        <span className="ml-2 text-xs text-gray-500">(OCI: {item.ociNumber})</span>
                      )}
                    </p>
                    {/* ── Staff-only cost detail ── */}
                    <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-500">
                      {item.pricePerLot != null && (
                        <span>Price Per Lot#: {item.pricePerLot}</span>
                      )}
                      {item.contractId && (
                        <span>Cargo Contract ID: {item.contractId}</span>
                      )}
                      {item.contractItemId && (
                        <span>Contract item ID: {String(item.contractItemId)}</span>
                      )}
                      {item.ociNumber && (
                        <span>OCI #: {item.ociNumber}</span>
                      )}
                      {item.lbsPerBag > 0 && (
                        <span>lbs/bag: {item.lbsPerBag}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center text-gray-700">{item.quantity}</td>
                  <td className="px-4 py-4 text-right text-gray-700">{fmt$(item.unitPrice)}</td>
                  <td className="px-4 py-4 text-right font-medium text-gray-900">{fmt$(item.totalPrice)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-500">No items found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ──────────────── SHIPPING DETAILS (staff-only) ──────────────── */}
        {hasShippingDetails && (
          <div className="mb-8">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Shipping Details</h2>
            <div className="grid grid-cols-1 gap-6 rounded-md border border-gray-100 bg-gray-50 p-4 md:grid-cols-2 print:grid-cols-2">
              {/* Left: accessorials */}
              <div className="space-y-1">
                {shipping.accessorialServices?.length > 0 && (
                  <>
                    <p className="font-semibold text-gray-900">Accessorial Services:</p>
                    <div className="space-y-1">
                      {shipping.accessorialServices.map((name: string) => (
                        <p key={name} className="text-sm text-gray-700">{name}</p>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Right: cost breakdown */}
              <div className="space-y-1 text-sm text-gray-700">
                {shipping.quoteId && (
                  <p><span className="font-semibold text-gray-900">Shipping Quote ID:</span> {shipping.quoteId}</p>
                )}
                {shippingQuote.quoteAmount !== undefined && (
                  <p><span className="font-semibold text-gray-900">Shipping quote amount:</span> {fmt$(shippingQuote.quoteAmount)}{shippingQuote.quoteEstimated ? ` (estimated from ${SHIPPING_MARKUP_POLICY_PCT}% policy)` : ""}</p>
                )}
                {showShippingCharged && (
                  <p><span className="font-semibold text-gray-900">Shipping charged to customer:</span> {fmt$(shipping.quotedToCustomer)}</p>
                )}
                {shippingQuote.markupAmount !== undefined && (
                  <p><span className="font-semibold text-gray-900">Markup{shippingQuote.markupPercent !== undefined ? ` (${shippingQuote.markupPercent}%)` : ""}:</span> {fmt$(shippingQuote.markupAmount)}</p>
                )}
                {shipping.pickupDate && (
                  <p><span className="font-semibold text-gray-900">Pickup Date:</span> {fmtShortDate(shipping.pickupDate)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ──────────────────────── TOTALS ──────────────────────── */}
        {totals && (totals.subtotal > 0 || totals.invoiceTotal > 0) && (
          <div className="flex justify-end">
            <div className="w-full max-w-sm space-y-2">
              {/* Subtotal + shipping + discounts */}
              <Row label="Subtotal" value={fmt$(totals.subtotal)} />
              {totals.shipping > 0 && <Row label="Shipping" value={fmt$(totals.shipping)} />}
              {totals.paymentMethodDiscount > 0 && (
                <Row label="Bank/ACH Discount" value={`-${fmt$(totals.paymentMethodDiscount)}`} valueClass="text-green-600" />
              )}
              {totals.paymentMethodFee > 0 && (
                <Row label="PayPal Fee (2%)" value={fmt$(totals.paymentMethodFee)} valueClass="text-amber-700" />
              )}
              {totals.promoCodeDiscount > 0 && (
                <Row
                  label={data.appliedPromoCode ? `Promo (${data.appliedPromoCode})` : "Promo Discount"}
                  value={`-${fmt$(totals.promoCodeDiscount)}`}
                  valueClass="text-green-600"
                />
              )}
              {totals.pointsDiscount > 0 && (
                <Row
                  label={`Points Applied${totals.pointsRedeemed ? ` (${Math.round(totals.pointsRedeemed)} pts)` : ""}`}
                  value={`-${fmt$(totals.pointsDiscount)}`}
                  valueClass="text-green-600"
                />
              )}
              {(totals.arCharges ?? []).map((charge: any) => (
                <Row
                  key={charge.id}
                  label={String(charge.label ?? "Additional Fee")}
                  value={fmt$(Number(charge.amount ?? 0))}
                />
              ))}

              {/* Invoice total */}
              <div className="mt-2 flex justify-between border-t border-gray-200 pt-3">
                <span className="text-base font-bold text-gray-900">Invoice total</span>
                <span className="text-lg font-bold text-gray-900">{fmt$(totals.invoiceTotal)}</span>
              </div>

              {totals.settledPayments > 0 && (
                <Row label="Settled payments" value={fmt$(totals.settledPayments)} />
              )}
              {Math.abs(totals.collectionDifference ?? 0) > 0.02 && (
                <Row
                  label={totals.collectionDifference > 0 ? "Over-collected" : "Under-collected"}
                  value={fmt$(Math.abs(totals.collectionDifference))}
                  valueClass="text-amber-700"
                />
              )}

              {/* Processing fee (staff-only cost data) */}
              {totals.stripeFee > 0 && (
                <>
                  <Row
                    label={totals.stripeFeeEstimated ? "Estimated processing fee" : "Less: Processing fee"}
                    sublabel={totals.stripeFeeMethodLabel ?? undefined}
                    value={`-${fmt$(totals.stripeFee)}`}
                    valueClass="text-red-600"
                  />
                  {totals.stripeFeeEstimated && !totals.processingFeeNote && (
                    <p className="text-right text-[11px] text-gray-400">Based on scheduled payment method</p>
                  )}
                </>
              )}
              {totals.processingFeeUnknown && (
                <Row
                  label={payment?.instrumentType === "credit_key" ? "Credit Key fee" : "Processing fee"}
                  value="Unknown"
                  valueClass="text-amber-700"
                />
              )}
              {totals.processingFeeNote && (
                <p className="text-right text-[11px] text-gray-500">{totals.processingFeeNote}</p>
              )}

              {/* Net after fees */}
              <div className="mt-1 flex justify-between border-t border-gray-300 pt-3">
                <span className="text-base font-bold text-gray-900">
                  {totals.processingFeeUnknown ? "Net after fees" : totals.netAfterFeesEstimated ? "Estimated net after fees" : "Net collected"}
                </span>
                <span className="text-lg font-bold text-green-600">{totals.processingFeeUnknown ? "Not available" : fmt$(totals.netAfterFees)}</span>
              </div>
            </div>
          </div>
        )}

        {hasReconciliationException && (
          <section className="mt-8 break-inside-avoid" aria-labelledby="payment-reconciliation-heading">
            <h2
              id="payment-reconciliation-heading"
              className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Payment reconciliation
            </h2>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-950">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">
                    {paymentReconciliation.status === "missing_payment_records"
                      ? "Paid status has no settled payment record"
                      : "Settled payments do not match the current invoice"}
                  </p>
                  <p className="mt-1 max-w-2xl text-sm text-amber-900">
                    {paymentReconciliation.status === "missing_payment_records"
                      ? "Review the payment provider before relying on this invoice for reconciliation."
                      : "Review the adjustment history below for the recorded cause and resolution."}
                  </p>
                </div>
                <Badge variant="outline" className="border-amber-300 bg-white text-amber-900">
                  {paymentReconciliation.status === "over_collected"
                    ? "Over-collected"
                    : paymentReconciliation.status === "under_collected"
                      ? "Under-collected"
                      : "Review needed"}
                </Badge>
              </div>
              <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3 print:grid-cols-3">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-amber-800">Current invoice</dt>
                  <dd className="mt-1 font-semibold">{fmt$(paymentReconciliation.invoiceTotal)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-amber-800">Settled payments</dt>
                  <dd className="mt-1 font-semibold">{fmt$(paymentReconciliation.settledPayments)}</dd>
                  {paymentReconciliation.refunds > 0 && (
                    <p className="mt-0.5 text-xs text-amber-800">
                      After {fmt$(paymentReconciliation.refunds)} in refunds
                    </p>
                  )}
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-amber-800">Difference</dt>
                  <dd className="mt-1 font-semibold">
                    {paymentReconciliation.difference > 0 ? "+" : ""}
                    {fmt$(paymentReconciliation.difference)}
                  </dd>
                </div>
              </dl>
            </div>
          </section>
        )}

        {auditEntries.length > 0 && (
          <section className="mt-8" aria-labelledby="internal-adjustment-history-heading">
            <h2
              id="internal-adjustment-history-heading"
              className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Internal adjustment history
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              Staff-only record of financial edits made after checkout.
            </p>
            <ol className="space-y-4">
              {auditEntries.map((entry: any) => (
                <InternalAuditEntry key={entry.id} entry={entry} />
              ))}
            </ol>
          </section>
        )}

        {/* ──────────────────────── FOOTER ──────────────────────── */}
        <div className="mt-10 border-t border-gray-200 pt-6 text-center">
          <p className="text-sm font-medium text-gray-900">Thank you for your business!</p>
          {!isPaid && (
            <p className="mt-2 text-sm text-amber-700">
              Please contact us if you have any questions about your order.
            </p>
          )}
          <p className="mt-3 text-xs text-gray-500">Anthem Coffee Imports</p>
        </div>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Small helper component for totals rows
// ---------------------------------------------------------------------------

function InternalAuditEntry({ entry }: { entry: any }) {
  const isShippingAdjustment = entry.type === "shipping_adjustment";
  const isOrderAmendment = entry.type === "order_amendment";
  const resolutionLabel = isShippingAdjustment
    ? formatResolutionMethod(entry.resolutionMethod)
    : null;

  return (
    <li className="break-inside-avoid rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-gray-900">{entry.title}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {fmtDateTime(entry.occurredAt)}
            {entry.actor ? ` · ${entry.actor}` : ""}
          </p>
        </div>
        {entry.occurredAfterPayment && (
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
            After payment
          </Badge>
        )}
      </div>

      {(isShippingAdjustment || isOrderAmendment) &&
        entry.previousAmount != null &&
        entry.newAmount != null && (
          <p className="mt-3 text-sm text-gray-800">
            <span className="font-medium">
              {isShippingAdjustment ? "Shipping charge:" : "Invoice total:"}
            </span>{" "}
            {fmt$(entry.previousAmount)} → {fmt$(entry.newAmount)}
          </p>
        )}

      {isOrderAmendment &&
        entry.beforeItemCount != null &&
        entry.afterItemCount != null &&
        entry.beforeItemCount !== entry.afterItemCount && (
          <p className="mt-1 text-sm text-gray-700">
            <span className="font-medium">Invoice lines:</span>{" "}
            {entry.beforeItemCount} → {entry.afterItemCount}
          </p>
        )}

      {entry.reason && (
        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700">
          {entry.reason}
        </p>
      )}

      {isShippingAdjustment && (
        <div className="mt-3 space-y-1 text-sm text-gray-700">
          <p>
            <span className="font-medium">Resolution:</span> {resolutionLabel}
            {entry.pointsCredited > 0
              ? ` — ${Math.round(entry.pointsCredited).toLocaleString("en-US")} points (${fmt$(entry.pointsCreditValue)})`
              : entry.resolutionAmount > 0
                ? ` — ${fmt$(entry.resolutionAmount)}`
                : ""}
            {entry.paymentLinkAmount > 0
              ? ` — ${fmt$(entry.paymentLinkAmount)} payment link${entry.paymentLinkStatus ? ` (${entry.paymentLinkStatus})` : ""}`
              : ""}
          </p>
          <p>
            <span className="font-medium">Customer notification:</span>{" "}
            <span className={entry.customerNotified ? "text-gray-700" : "font-medium text-amber-700"}>
              {entry.customerNotified ? "Sent" : "Not sent"}
            </span>
          </p>
        </div>
      )}

      {isOrderAmendment && entry.pointsCredited > 0 && (
        <p className="mt-3 text-sm text-gray-700">
          <span className="font-medium">Resolution:</span>{" "}
          {Math.round(entry.pointsCredited).toLocaleString("en-US")} points credited
          ({fmt$(entry.pointsCreditValue)})
        </p>
      )}
    </li>
  );
}

function Row({
  label,
  sublabel,
  value,
  valueClass,
}: {
  label: string;
  sublabel?: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex justify-between border-b border-gray-100 pb-2 text-sm">
      <span className="text-gray-600">
        {label}
        {sublabel && <span className="ml-1 text-xs text-gray-400">({sublabel})</span>}
      </span>
      <span className={`font-medium ${valueClass ?? "text-gray-900"}`}>{value}</span>
    </div>
  );
}
